import React, { useEffect, useState } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { Button, Typography, PhoneNumber, Form, Input } from '@crisellevaldez/speculo-ui';
import {
  signInWithEmailAndPassword,
  signInWithPopup,
  GoogleAuthProvider,
} from 'firebase/auth';
import { auth } from '../lib/firebase';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { signupUser, validateUser } from '../api/remiApi';
import { validationRejectionMessage } from '../utils/loginErrorCopy';
import { SUPPORT_CONTACT } from '../constants/supportContact';
import { setCachedOrganization, useOrganizationStore } from '../stores/useOrganizationStore';

const signupSchema = z.object({
  email: z.string().min(1, 'Email is required').email('Invalid email format'),
  password: z.string().min(6, 'Password must be at least 6 characters'),
  firstName: z.string().min(1, 'First name is required'),
  lastName: z.string().min(1, 'Last name is required'),
  phoneNumber: z.string().min(10, 'Valid phone number is required'),
});

type SignupFormData = z.infer<typeof signupSchema>;

export const SignupPage: React.FC = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const orgIdFromUrl = searchParams.get('org_id') ?? searchParams.get('org') ?? '';
  const emailFromUrl = searchParams.get('email') ?? '';

  const { isAuthenticated, organization, error: authError } = useOrganizationStore();

  const [error, setError] = useState<string>('');
  const [loading, setLoading] = useState(false);

  // If a session is already active with an org loaded, signup shouldn't be reachable.
  useEffect(() => {
    if (isAuthenticated && organization && !authError) {
      navigate('/dashboard', { replace: true });
    }
  }, [isAuthenticated, organization, authError, navigate]);

  const completeSignup = async (orgId: string) => {
    setCachedOrganization({ id: orgId });
    await useOrganizationStore.getState().fetchOrganization();
    navigate('/dashboard', { replace: true });
  };

  const {
    register,
    handleSubmit,
    setValue,
    watch,
    formState: { errors },
  } = useForm<SignupFormData>({
    resolver: zodResolver(signupSchema),
    defaultValues: {
      email: emailFromUrl,
    },
  });

  const onSignupSubmit = async (data: SignupFormData) => {
    setLoading(true);
    setError('');

    try {
      await signupUser({
        email: data.email,
        org_id: orgIdFromUrl,
        password: data.password,
        first_name: data.firstName,
        last_name: data.lastName,
        phone: data.phoneNumber,
      });

      await signInWithEmailAndPassword(auth, data.email, data.password);

      const validation = await validateUser();
      if (!validation.valid) {
        await auth.signOut();
        throw new Error(
          validationRejectionMessage(
            validation.reason,
            'Your account is not authorized. Please contact your administrator.'
          )
        );
      }
      await completeSignup((validation.user?.organization_id as string) || orgIdFromUrl);
    } catch (err) {
      console.error('Signup error:', err);
      setError(err instanceof Error ? err.message : 'An error occurred during signup');
      setLoading(false);
    }
  };

  const handleGoogleSignup = async () => {
    setLoading(true);
    setError('');

    let signedIn = false;

    try {
      const provider = new GoogleAuthProvider();
      const { user } = await signInWithPopup(auth, provider);
      signedIn = true;

      const email = user.email;
      if (!email) throw new Error('No email provided from Google');

      const nameParts = user.displayName?.split(' ') || ['', ''];
      const firstName = nameParts[0];
      const lastName = nameParts.slice(1).join(' ') || '';

      await signupUser({
        email,
        org_id: orgIdFromUrl,
        firebase_uid: user.uid,
        first_name: firstName,
        last_name: lastName,
        phone: user.phoneNumber || '',
      });

      const validation = await validateUser();
      if (!validation.valid) {
        await auth.signOut();
        throw new Error(
          validationRejectionMessage(
            validation.reason,
            'Your account is not authorized. Please contact your administrator.'
          )
        );
      }
      await completeSignup((validation.user?.organization_id as string) || orgIdFromUrl);
    } catch (err) {
      console.error('Google signup error:', err);
      if (signedIn) {
        try { await auth.signOut(); } catch { /* best-effort cleanup */ }
      }
      setError(err instanceof Error ? err.message : 'An error occurred during signup');
      setLoading(false);
    }
  };

  return (
    <div className="flex flex-col items-center justify-center min-h-screen p-4 bg-background">
      <div className="flex justify-center mb-8">
        <img src="/speculo.ai-black.png" alt="Speculo AI" className="h-12 dark:hidden" />
        <img src="/speculo.ai-white.png" alt="Speculo AI" className="h-12 hidden dark:block" />
      </div>
      <div className="w-full max-w-md space-y-6 bg-card rounded-lg shadow-xl p-8 mb-6 border border-border">
        <div className="text-center">
          <Typography variant="h1" className="text-3xl font-bold">
            Create Your Account
          </Typography>
          <p className="text-muted-foreground mt-2">Fill in your details to get started</p>
        </div>

        {error && (
          <div className="p-4 text-sm text-red-700 dark:text-red-300 bg-red-100 dark:bg-red-900/30 rounded-lg" role="alert">
            {error}
          </div>
        )}

        <form onSubmit={handleSubmit(onSignupSubmit)} className="space-y-4 mt-8" noValidate>
          <Form.Control>
            <Form.Label required>Email</Form.Label>
            <Input
              type="email"
              error={errors.email?.message}
              placeholder="Enter your email"
              readOnly={!!emailFromUrl}
              {...register('email')}
            />
          </Form.Control>

          <Form.Control>
            <Form.Label required>First Name</Form.Label>
            <Input
              type="text"
              error={errors.firstName?.message}
              placeholder="First Name"
              {...register('firstName')}
            />
          </Form.Control>

          <Form.Control>
            <Form.Label required>Last Name</Form.Label>
            <Input
              type="text"
              error={errors.lastName?.message}
              placeholder="Last Name"
              {...register('lastName')}
            />
          </Form.Control>

          <Form.Control>
            <Form.Label required>Phone Number</Form.Label>
            <PhoneNumber
              error={errors.phoneNumber?.message}
              value={watch('phoneNumber') || ''}
              onChange={(value) => {
                setValue('phoneNumber', value || '', { shouldValidate: true });
              }}
            />
          </Form.Control>

          <Form.Control>
            <Form.Label required>Password</Form.Label>
            <Input
              type="password"
              error={errors.password?.message}
              placeholder="Create Password"
              {...register('password')}
            />
          </Form.Control>

          <Button
            type="submit"
            variant="primary"
            className="w-full mt-2"
            isLoading={loading}
            disabled={loading}
          >
            Create Account
          </Button>
        </form>

        <div className="relative mt-4">
          <div className="absolute inset-0 flex items-center">
            <div className="w-full border-t border-border"></div>
          </div>
          <div className="relative flex justify-center text-sm">
            <span className="px-4 text-muted-foreground bg-card">Or continue with</span>
          </div>
        </div>

        <Button
          variant="secondary"
          onClick={handleGoogleSignup}
          className="w-full"
          disabled={loading}
        >
          <img
            src="https://www.gstatic.com/firebasejs/ui/2.0.0/images/auth/google.svg"
            alt="Google"
            className="w-5 h-5 mr-2"
          />
          Sign up with Google
        </Button>
      </div>
      <div className="w-full max-w-md p-4 bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-700/30 rounded-lg">
        <div className="space-y-2">
          <p className="font-medium text-amber-800 dark:text-amber-300">
            Need help?
          </p>
          <p className="text-sm text-amber-700 dark:text-amber-400">
            Trouble signing up or didn't receive an invite? Contact{' '}
            <a
              href={`mailto:${SUPPORT_CONTACT.email}`}
              className="text-amber-800 dark:text-amber-300 hover:text-amber-900 dark:hover:text-amber-200 underline font-medium"
            >
              {SUPPORT_CONTACT.email}
            </a>{' '}
            and we'll help you out.
          </p>
        </div>
      </div>
    </div>
  );
};
